/************************************************************************************************************
Author                          : Trizent Technologies Private Limited
Date                            : 
Notes/Remarks                   : 

Organization:  	Trizent Technologies Private Limited.
		Module No. 17, SBC, Thejaswini Building
		Phase 1, Technopark, Thiruvananthapuram - 695581, 
		Kerala, India
 
Copyright 2026 (C) Trizent Technologies Pvt Ltd. (www.trizentinc.com)
All rights reserved.
***************************************************************************************************************/

// ObservationSchedule.tsx
import { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";

interface ObservationDay {
  id: number;
  day: string;
  enabled: boolean;
}

export function useObservationSchedule() {
  const [scheduleEnabled, setScheduleEnabled] = useState(true);
  const [days, setDays] = useState<ObservationDay[]>([]); // was pre-filled, now empty

  const toggleDayEnabled = (id: number) =>
    setDays((prev) =>
      prev.map((d) => (d.id === id ? { ...d, enabled: !d.enabled } : d))
    );

  const deleteDay = (id: number) =>
    setDays((prev) => prev.filter((d) => d.id !== id));

  const clearAll = () => {
    setDays([]);
  }

  // now takes the day number typed by the user instead of auto-incrementing
  const addDay = (dayNumber: number) => {
    const nextId = days.length ? Math.max(...days.map((d) => d.id)) + 1 : 1;
    setDays((prev) => [
      ...prev,
      { id: nextId, day: `${dayNumber}`, enabled: true },
    ]);
  };

  return { scheduleEnabled, setScheduleEnabled, days, toggleDayEnabled, deleteDay, addDay, clearAll, setDays };
}

// Just the toggle — goes inline in the grid cell
export function ObservationScheduleToggle({
  enabled,
  onChange,
}: {
  enabled: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className="flex min-w-0 flex-1 items-center gap-2 text-slate-700">
      <span className="text-xs truncate">Enable Observation Schedule</span>
      <button
        type="button"
        role="switch"
        aria-checked={enabled}
        onClick={() => onChange(!enabled)}
        className={`relative inline-flex h-4 w-7 shrink-0 items-center rounded-full transition-colors ${enabled ? "bg-emerald-500" : "bg-slate-300"
          }`}
      >
        <span
          className={`inline-block h-3 w-3 transform rounded-full bg-white shadow transition-transform ${enabled ? "translate-x-3.5" : "translate-x-0.5"
            }`}
        />
      </button>
    </label>
  );
}

// The table — goes full-width, outside the grid
export function ObservationScheduleTable({
  enabled,
  days,
  toggleDayEnabled,
  deleteDay,
  addDay,
  maxDay,
}: {
  enabled: boolean;
  days: ObservationDay[];
  maxDay: String;
  toggleDayEnabled: (id: number) => void;
  deleteDay: (id: number) => void;
  addDay: (dayNumber: number) => void;
}) {
  const [newDay, setNewDay] = useState(""); // NEW
  const [error, setError] = useState("");   // NEW

  // Get the highest day number currently in the list
  const getMaxExistingDayNumber = () => {
    if (!days.length) return 0;
    const dayNumbers = days.map((d) => {
      const match = d.day.match(/\d+/); // extract number from "Day X"
      return match ? Number(match[0]) : 0;
    });
    return Math.max(...dayNumbers);
  };

  const maxExistingDay = getMaxExistingDayNumber();

  // Check if maxDay is a valid positive number
  const isMaxDayValid = maxDay !== "" && !isNaN(Number(maxDay)) && Number(maxDay) > 0;

  // Check if the day the user typed already exists in the days list
  const dayAlreadyExists = days.some((d) => d.day === `Day ${newDay}`);

  // Check if newDay is less than or equal to the highest existing day
  const isDayTooLow = days.length > 0 && Number(newDay) <= maxExistingDay;

  // Check if newDay itself is valid (non-empty, numeric, within range)
  const isNewDayValid =
    newDay !== "" &&
    !isNaN(Number(newDay)) &&
    Number(newDay) >= 1 &&
    Number(newDay) <= Number(maxDay);

  const isAddDisabled =
    !isMaxDayValid || !isNewDayValid || dayAlreadyExists || isDayTooLow;

  useEffect(() => {
    if (!isMaxDayValid) {
      setError("Please enter a valid max day first");
    } else if (dayAlreadyExists) {
      setError(`Day ${newDay} already exists`);
    } else if (isDayTooLow) {
      setError(`Day must be greater than ${maxExistingDay}`);
    } else if (newDay !== "" && Number(newDay) > Number(maxDay)) {
      setError(`Day cannot exceed ${maxDay}`);
    } else if (newDay !== "" && !isNewDayValid) {
      setError("Please enter a valid day number");
    } else {
      setError("");
    }
  }, [newDay, maxDay, days, isMaxDayValid, dayAlreadyExists, isDayTooLow, isNewDayValid, maxExistingDay]);

  const handleAdd = () => {
    const value = Number(newDay);
    if (!newDay || isNaN(value) || value <= 0) {
      setError("Enter a valid day number");
      return;
    }
    if (!maxDay) {
      setError("Enter activity duration days");
      return;
    }
    addDay(value);
    setNewDay("");
    setError("");
  };
  return (
    <div className={`w-full rounded-md border border-slate-200 bg-white ${enabled ? "" : "pointer-events-none opacity-50"}`}>
      {days.length > 0 && (
        <div className="flex items-center gap-2 border-b border-slate-200 px-3 py-1.5 text-[11px] font-medium text-slate-500">
          <span className="w-5 shrink-0">Seq</span>
          <span className="flex-1">Observation Day</span>
          <span className="w-14 shrink-0 text-right">Action</span>
        </div>
      )}

      {/* <ul className="divide-y divide-slate-50"> */}
      <div className="overflow-scroll max-h-[15vh]">
      {days.map((d, idx) => (
        <div key={d.id} className="flex items-center gap-2 px-3 py-2 text-[13px]">
          <span className="w-5 shrink-0 text-slate-500">{idx + 1}</span>
          <label className="min-w-0 flex-1 items-center gap-2 text-slate-700">
            {/* <input
              type="checkbox"
              checked={d.enabled}
              onChange={() => toggleDayEnabled(d.id)}
              className="h-3.5 w-3.5 shrink-0 rounded border-slate-300 text-blue-600 focus:ring-blue-500"
            /> */}
            <span className="truncate m-1">Day {d.day}</span>
          </label>
          <button
            type="button"
            onClick={() => deleteDay(d.id)}
            aria-label={`Delete ${d.day}`}
            className="flex w-14 shrink-0 items-center justify-end gap-1 text-xs font-medium text-blue-600 hover:text-blue-700"
          >
            <Trash2 className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Delete</span>
          </button>
        </div>
      ))}
      </div>
      {/* </ul> */}

      <div className="px-3 py-2 mx-4">
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-500 shrink-0">Day</span>
          <input
            type="number"
            min={1}
            max={Number(maxDay)}
            value={newDay}
            onChange={(e) => {
              const value = e.target.value;

              // allow empty string so user can clear and retype
              if (value === "") {
                setNewDay(value);
                if (error) setError("");
                return;
              }

              const numValue = Number(value);

              // ignore invalid (non-numeric) input
              if (isNaN(numValue)) {
                return;
              }

              // block values greater than maxDay
              if (numValue > Number(maxDay)) {
                setNewDay(String(maxDay));
                setError(`Value cannot be greater than activity duration days`);
                return;
              }

              // block negative or zero values (since min=1)
              if (numValue < 1) {
                setNewDay(value); // let them keep typing, validate on blur instead
                setError("Value must be at least 1");
                return;
              }

              setNewDay(value);
              if (error) setError("");
            }}
            onBlur={() => {
              const numValue = Number(newDay);
              if (newDay === "" || isNaN(numValue) || numValue < 1) {
                setError("Please enter a valid value ");
              }
            }}
            placeholder="e.g. 3"
            className={`h-7 w-20 rounded border px-2 text-xs focus:outline-none ${error
              ? "border-red-500 focus:border-red-500"
              : "border-slate-300 focus:border-blue-500"
              }`}
          />

          <button
            type="button"
            disabled={isAddDisabled}
            onClick={handleAdd}
            className={`flex items-center gap-1.5 text-xs font-medium ${isAddDisabled
                ? "text-slate-400 cursor-not-allowed"
                : "text-blue-600 hover:text-blue-700"
              }`}
          >
            <span
              className={`flex h-4 w-4 items-center justify-center rounded-full text-white ${isAddDisabled ? "bg-slate-300" : "bg-blue-600"
                }`}
            >
              <Plus className="h-2.5 w-2.5" />
            </span>
            Add Observation Day
          </button>         
        </div>
        {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
      </div>


      {/* <div className="px-3 py-2">
        <button
          type="button"
          onClick={addDay}
          className="flex items-center gap-1.5 text-xs font-medium text-blue-600 hover:text-blue-700"
        >
          <span className="flex h-4 w-4 items-center justify-center rounded-full bg-blue-600 text-white">
            <Plus className="h-2.5 w-2.5" />
          </span>
          Add Observation Day
        </button>
      </div> */}
    </div>
  );
}